// C:\Users\purplehat\Desktop\payment-cookie-system\frontend\src\components\SmsManager.js

import React, { useState, useEffect, useRef } from 'react';
import {
  PhoneIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowLeftIcon,
  XMarkIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import api from '../services/api';

function SmsManager() {
  const [balance, setBalance] = useState(null);
  const [usage, setUsage] = useState(null);
  const [activeActivation, setActiveActivation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [waitingForSms, setWaitingForSms] = useState(false);
  const [smsCode, setSmsCode] = useState(null);
  const [hasPermission, setHasPermission] = useState(true);

  // ✅ Selector de proveedores
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [providersError, setProvidersError] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const waitingInterval = useRef(null);

  useEffect(() => {
    checkPermissionAndLoad();
    return () => {
      if (waitingInterval.current) clearInterval(waitingInterval.current);
    };
  }, []);

  const checkPermissionAndLoad = async () => {
    const permitted = await checkSmsPermissions();
    setHasPermission(permitted);
    if (permitted) loadData();
  };

  const loadData = async () => {
    try {
      const [balanceRes, usageRes, activeRes] = await Promise.all([
        api.get('/sms/balance'),
        api.get('/sms/usage'),
        api.get('/sms/active')
      ]);

      if (balanceRes.data.success) setBalance(balanceRes.data.balance);
      if (usageRes.data.success) setUsage(usageRes.data.usage);
      if (activeRes.data.success && activeRes.data.activations.length > 0) {
        const active = activeRes.data.activations[0];
        setActiveActivation(active);
        if (active.status === 'WAITING_SMS') startSmsPolling(active._id);
      }
    } catch (error) {
      if (error.response?.status === 403) setHasPermission(false);
      console.error('Error loading data:', error);
    }
  };

  const checkSmsPermissions = async () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      if (user.role === 'owner' || user.role === 'admin') return true;
      try {
        const response = await api.get('/auth/me');
        if (response.data?.user?.permissions?.canUseSms) {
          const updatedUser = { ...user, permissions: response.data.user.permissions };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return true;
        }
      } catch (apiError) {
        if (user.permissions?.canUseSms) return true;
      }
      return false;
    } catch (e) { return false; }
  };

  const startSmsPolling = (activationId) => {
    if (waitingInterval.current) clearInterval(waitingInterval.current);
    setWaitingForSms(true);

    waitingInterval.current = setInterval(async () => {
      try {
        const response = await api.get(`/sms/check-sms/${activationId}`);
        if (response.data.status === 'COMPLETED') {
          clearInterval(waitingInterval.current);
          setWaitingForSms(false);
          setSmsCode(response.data.code);
          toast.success(`Código SMS recibido: ${response.data.code}`);
          loadData();
        } else if (response.data.status === 'CANCELED') {
          clearInterval(waitingInterval.current);
          setWaitingForSms(false);
          toast.error('Activación cancelada');
          loadData();
        }
      } catch (error) {
        console.error('Error polling SMS:', error);
      }
    }, 5000);
  };

  // ✅ 1. Abrir modal y cargar lista real de proveedores
  const openProviderSelector = async () => {
    const permitted = await checkSmsPermissions();
    if (!permitted) {
      setHasPermission(false);
      toast.error('Ya no tienes permiso para usar SMS.');
      return;
    }

    setShowProviderModal(true);
    setProviders([]);
    setProvidersError(null);
    setLoadingProviders(true);

    try {
      const res = await api.get('/sms/prices?service=am');
      if (res.data.success) {
        setProviders(res.data.providers || []);
        if ((res.data.providers || []).length === 0) {
          setProvidersError('No hay proveedores con stock en este momento.');
        }
      } else {
        setProvidersError(res.data.error || 'No se pudieron cargar los precios.');
      }
    } catch (error) {
      setProvidersError(
        error.response?.data?.error || 'No se pudieron cargar los precios. Intenta de nuevo.'
      );
    } finally {
      setLoadingProviders(false);
    }
  };

  // ✅ 2. Comprar con el proveedor elegido
  const buyFromProvider = async (provider) => {
    setSelectedProvider(provider);
    setLoading(true);
    try {
      const response = await api.post('/sms/request-number', {
        service: 'am',
        providerId: provider.provider
      });

      if (response.data.success) {
        setActiveActivation(response.data.activation);
        toast.success(`Número obtenido: ${response.data.activation.phoneNumber}`);
        setShowProviderModal(false);
        setSelectedProvider(null);
        loadData();
      } else {
        toast.error(response.data.error || 'No se pudo obtener el número');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Error al solicitar número';
      toast.error(msg);
      if (error.response?.status === 403) setHasPermission(false);
      // Si el provider se quedó sin stock, recargar la lista
      if (error.response?.status === 400) {
        const res = await api.get('/sms/prices?service=am').catch(() => null);
        if (res?.data?.success) setProviders(res.data.providers || []);
      }
    } finally {
      setLoading(false);
      setSelectedProvider(null);
    }
  };

  const confirmNumber = async () => {
    if (!activeActivation) return;
    setLoading(true);
    try {
      const response = await api.post('/sms/confirm-number', {
        activationId: activeActivation._id
      });
      if (response.data.success) {
        toast.success(`Número confirmado: ${response.data.phoneNumber}`);
        await loadData();
        startSmsPolling(activeActivation._id);
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al confirmar');
      if (error.response?.status === 403) setHasPermission(false);
    } finally {
      setLoading(false);
    }
  };

  const cancelActivation = async () => {
    if (!activeActivation) return;
    if (!window.confirm('¿Cancelar esta activación?')) return;
    setLoading(true);
    try {
      const response = await api.post('/sms/cancel', {
        activationId: activeActivation._id
      });
      if (response.data.success) {
        toast.success('Activación cancelada');
        setActiveActivation(null);
        setWaitingForSms(false);
        setSmsCode(null);
        if (waitingInterval.current) clearInterval(waitingInterval.current);
        loadData();
      } else {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al cancelar');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (p) => (typeof p === 'number' ? `$${p.toFixed(4)}` : '—');
  const sortedProviders = [...providers].sort((a, b) => {
    const pa = typeof a.price === 'number' ? a.price : Infinity;
    const pb = typeof b.price === 'number' ? b.price : Infinity;
    return pa - pb;
  });
  const cheapestPrice = sortedProviders.find(p => typeof p.price === 'number')?.price;

  // ===== Acceso restringido =====
  if (!hasPermission) {
    return (
      <div style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
        <XCircleIcon style={{ width: '4rem', height: '4rem', color: '#ff0033', marginBottom: '1rem' }} />
        <h2 style={{ marginBottom: '1rem' }}>Acceso Restringido</h2>
        <p style={{ color: '#9ca3af', marginBottom: '2rem' }}>
          No tienes permiso para usar el servicio de SMS.<br />Contacta al administrador para solicitarlo.
        </p>
        <button onClick={() => window.history.back()} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.75rem', color: '#fff', cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: '1.25rem' }} /> Volver
        </button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', padding: '1rem', paddingBottom: '6rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 0', marginBottom: '2rem' }}>
        <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: '#00ffff', cursor: 'pointer' }}>
          <ArrowLeftIcon style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #00ffff, #ff00ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          SMS Manager - Amazon
        </h1>
      </div>

      {/* Saldo */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a1a, #0a0a0a)', border: '1px solid #333', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <CreditCardIcon style={{ width: '1.5rem', height: '1.5rem', color: '#00ffff' }} />
          <span style={{ color: '#9ca3af' }}>Saldo disponible</span>
        </div>
        <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffff' }}>
          ${balance !== null ? balance.toFixed(2) : '...'}
        </div>
      </div>

      {/* Uso */}
      {usage && (
        <div style={{ background: '#111111', border: '1px solid #222222', borderRadius: '1rem', padding: '1rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#9ca3af' }}>Números usados hoy</span>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{usage.current}/{usage.limit}</span>
          </div>
          {usage.remainingHours > 0 && (
            <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Reinicia en {usage.remainingHours} horas
            </div>
          )}
        </div>
      )}

      {/* Activación activa */}
      {activeActivation && (
        <div style={{ background: '#111111', border: `1px solid ${activeActivation.status === 'WAITING_SMS' ? '#ffaa00' : '#00ffff'}`, borderRadius: '1rem', padding: '1.5rem', marginBottom: '1rem' }}>
          <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>
            {activeActivation.status === 'WAITING_SMS' ? '⏳ Esperando SMS' : '📱 Activación activa'}
          </h3>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Número</div>
            <div style={{ fontSize: '1.25rem', fontFamily: 'monospace' }}>{activeActivation.phoneNumber}</div>
            {activeActivation.provider && (
              <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                Proveedor #{activeActivation.provider}
              </div>
            )}
          </div>

          {waitingForSms && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', color: '#ffaa00' }}>
              <ArrowPathIcon style={{ width: '1rem', height: '1rem', animation: 'spin 1s linear infinite' }} />
              <span>Esperando código SMS...</span>
            </div>
          )}

          {smsCode && (
            <div style={{ background: '#00ffff10', border: '1px solid #00ffff', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
              <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Código recibido</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00ffff', fontFamily: 'monospace' }}>{smsCode}</div>
            </div>
          )}

          {activeActivation.status === 'ACTIVE' && (
            <button onClick={confirmNumber} disabled={loading} style={{ width: '100%', padding: '0.75rem', background: '#00ffff', border: 'none', borderRadius: '0.5rem', color: '#000000', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '0.5rem' }}>
              Confirmar y esperar SMS
            </button>
          )}

          <button onClick={cancelActivation} disabled={loading} style={{ width: '100%', padding: '0.75rem', background: 'none', border: '1px solid #ff0033', borderRadius: '0.5rem', color: '#ff0033', cursor: loading ? 'not-allowed' : 'pointer' }}>
            Cancelar activación
          </button>
        </div>
      )}

      {/* Botón para abrir selector de proveedores */}
      {!activeActivation && (
        <button onClick={openProviderSelector} disabled={loading} style={{ width: '100%', padding: '1rem', background: 'linear-gradient(135deg, #00ffff, #0088ff)', border: 'none', borderRadius: '1rem', color: '#000000', fontWeight: 'bold', fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer' }}>
          🔢 Ver precios y solicitar número
        </button>
      )}

      {/* Historial */}
      {usage?.history && usage.history.length > 0 && (
        <div style={{ background: '#111111', border: '1px solid #222222', borderRadius: '1rem', padding: '1rem', marginTop: '1rem' }}>
          <h3 style={{ color: '#00ffff', marginBottom: '1rem' }}>Historial reciente</h3>
          {usage.history.slice(-5).reverse().map((item, idx) => (
            <div key={idx} style={{ padding: '0.75rem', borderBottom: '1px solid #222222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{item.phoneNumber}</div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{new Date(item.date).toLocaleString()}</div>
              </div>
              <div>
                {item.status === 'COMPLETED' ? (
                  <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#00ff00' }} />
                ) : (
                  <XCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#ff0033' }} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ MODAL SELECTOR DE PROVEEDORES */}
      {showProviderModal && (
        <div
          onClick={() => { if (!loading) { setShowProviderModal(false); setSelectedProvider(null); } }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: '1.25rem', width: '100%', maxWidth: 520, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            {/* Header modal */}
            <div style={{ padding: '1.25rem 1.25rem 0.75rem', borderBottom: '1px solid #1a1a1a', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', margin: 0 }}>Proveedores disponibles</h3>
                <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
                  Amazon · USA · Elige el precio que prefieras
                </p>
              </div>
              <button
                onClick={() => { if (!loading) { setShowProviderModal(false); setSelectedProvider(null); } }}
                disabled={loading}
                style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: '0.5rem', padding: '0.35rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex' }}
              >
                <XMarkIcon style={{ width: '1.1rem', color: '#9ca3af' }} />
              </button>
            </div>

            {/* Body modal */}
            <div style={{ padding: '0.75rem 1.25rem 1.25rem', overflowY: 'auto', flex: 1 }}>
              {loadingProviders && (
                <div style={{ textAlign: 'center', padding: '2.5rem 0' }}>
                  <ArrowPathIcon style={{ width: '1.75rem', height: '1.75rem', color: '#00ffff', margin: '0 auto 0.5rem', animation: 'spin 1s linear infinite' }} />
                  <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Cargando precios…</div>
                </div>
              )}

              {!loadingProviders && providersError && (
                <div style={{ background: '#ff003310', border: '1px solid #ff003340', borderRadius: '0.75rem', padding: '1rem', textAlign: 'center' }}>
                  <XCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: '#ff3366', margin: '0 auto 0.5rem' }} />
                  <div style={{ color: '#ff8095', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{providersError}</div>
                  <button onClick={openProviderSelector} style={{ padding: '0.5rem 1rem', background: '#00ffff', border: 'none', borderRadius: '0.5rem', color: '#000', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                    Reintentar
                  </button>
                </div>
              )}

              {!loadingProviders && !providersError && sortedProviders.length === 0 && (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: '#9ca3af', fontSize: '0.85rem' }}>
                  No hay proveedores con stock ahora mismo.
                </div>
              )}

              {!loadingProviders && !providersError && sortedProviders.map((p) => {
                const isCheapest = typeof p.price === 'number' && p.price === cheapestPrice;
                const isBuying = loading && selectedProvider?.provider === p.provider;
                return (
                  <button
                    key={p.provider}
                    onClick={() => buyFromProvider(p)}
                    disabled={loading}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.85rem 0.9rem', marginBottom: '0.5rem', borderRadius: '0.75rem',
                      background: isCheapest ? 'rgba(0,255,255,0.06)' : '#111111',
                      border: `1px solid ${isCheapest ? 'rgba(0,255,255,0.4)' : '#222'}`,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading && !isBuying ? 0.5 : 1,
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = isCheapest ? 'rgba(0,255,255,0.1)' : '#1a1a1a'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isCheapest ? 'rgba(0,255,255,0.06)' : '#111111'; }}
                  >
                    <div style={{ width: 40, height: 40, borderRadius: '0.6rem', background: isCheapest ? 'rgba(0,255,255,0.15)' : '#1a1a1a', border: `1px solid ${isCheapest ? 'rgba(0,255,255,0.3)' : '#2a2a2a'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <PhoneIcon style={{ width: '1.1rem', color: isCheapest ? '#00ffff' : '#9ca3af' }} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.15rem' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>Proveedor #{p.provider}</span>
                        {isCheapest && (
                          <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#00ffff', background: 'rgba(0,255,255,0.12)', padding: '0.1rem 0.4rem', borderRadius: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Más barato
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                        Stock: {p.count === Infinity || p.count === undefined ? 'disponible' : `${p.count} números`}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: isCheapest ? '#00ffff' : '#fff', fontFamily: 'monospace' }}>
                        {formatPrice(p.price)}
                      </div>
                      <div style={{ fontSize: '0.65rem', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.15rem' }}>
                        {isBuying ? (
                          <><ArrowPathIcon style={{ width: '0.7rem', animation: 'spin 1s linear infinite' }} /> Comprando…</>
                        ) : (
                          <>Comprar <ChevronRightIcon style={{ width: '0.7rem' }} /></>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer modal */}
            <div style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid #1a1a1a', background: '#080808' }}>
              <button
                onClick={openProviderSelector}
                disabled={loading || loadingProviders}
                style={{ width: '100%', padding: '0.6rem', background: 'transparent', border: '1px solid #333', borderRadius: '0.6rem', color: '#9ca3af', fontSize: '0.8rem', cursor: loading || loadingProviders ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
              >
                <ArrowPathIcon style={{ width: '0.9rem' }} /> Actualizar precios
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SmsManager;
